import React from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import MenuItem from '~/components/core/Atomic/MenuItem';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import MultiSelectAutoCompleteFormik from '~/components/core/Formik/MultiSelectAutoCompleteFormik/MultiSelectAutoCompleteFormik';
import YesNoNaUnknownQuestionFormik from '~/components/core/Formik/YesNoNaUnknownQuestionFormik';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { DatePickerTextFieldFormik, TextFieldFormik } from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  MetabaseParameterValuesResponse,
  ReportTemplateListResponseMetabaseParameter,
  ReportTemplateListResponseReportTemplateResponse,
} from '~/server_shared/openapi/data-contracts';

import { useReportTemplates } from '../ReportTemplates/hooks/useReportTemplates';

export type ReportParameters = Record<string, unknown>;

export function stripEmptyParameters(parameters: ReportParameters): ReportParameters | null {
  const filtered = Object.fromEntries(
    Object.entries(parameters).filter(
      ([, v]) => v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
    )
  );
  return Object.keys(filtered).length > 0 ? filtered : null;
}

interface ParameterWithQuestionId extends ReportTemplateListResponseMetabaseParameter {
  metabase_question_id: number;
}

interface ReportParameterFieldsProps {
  disabled?: boolean;
}

export function collectUniqueParameters(
  template: ReportTemplateListResponseReportTemplateResponse | undefined
): ParameterWithQuestionId[] {
  if (!template?.sheets) return [];

  const seen = new Set<string>();
  const params: ParameterWithQuestionId[] = [];

  for (const sheet of template.sheets) {
    if (!sheet.metabase_parameters) continue;
    for (const param of sheet.metabase_parameters) {
      if (!param.id || seen.has(param.id)) continue;
      seen.add(param.id);
      params.push({ ...param, metabase_question_id: sheet.metabase_question_id });
    }
  }

  return params;
}

export function formatParamLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function createParametersValidation(templates: ReportTemplateListResponseReportTemplateResponse[]) {
  return Yup.mixed<ReportParameters>().test(
    'required-parameters',
    'Please fill in all required filters',
    function (parameters) {
      const { report_template_id } = this.parent;
      const template = templates.find((t) => t.id === report_template_id);
      if (!template) return true;

      const requiredParams = collectUniqueParameters(template).filter((p) => p.required);
      if (requiredParams.length === 0) return true;

      const errors: Yup.ValidationError[] = [];
      for (const param of requiredParams) {
        const value = parameters?.[param.id];
        const isEmpty =
          value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          errors.push(
            this.createError({
              path: `parameters.${param.id}`,
              message: `${formatParamLabel(param.name)} is required`,
            })
          );
        }
      }

      if (errors.length > 0) throw new Yup.ValidationError(errors);
      return true;
    }
  );
}

const ListParameterField: React.FC<{
  param: ParameterWithQuestionId;
  disabled: boolean;
}> = ({ param, disabled }) => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();

  const url = `/sysconfig/api/v1/organizations/${organization.id}/reports/metabase-questions/${param.metabase_question_id}/params/${param.id}/values`;
  const { data, isLoading, isError } = useDataFetcher<MetabaseParameterValuesResponse>(url);
  const options = (data?.values ?? []).map((v) => String(v[0])).filter(Boolean);

  const label = formatParamLabel(param.name);
  const fieldId = `parameters.${param.id}`;

  if (isError) {
    return (
      <div>
        <TextFieldFormik
          id={fieldId}
          label={label}
          fullWidth
          disabled={disabled}
          helperText="Could not load options. You may type a value manually."
        />
      </div>
    );
  }

  return (
    <div>
      {param.isMultiSelect ? (
        <MultiSelectAutoCompleteFormik
          id={fieldId}
          label={label}
          options={options}
          getOptionLabel={(opt: string) => opt}
          disabled={disabled || isLoading}
        />
      ) : (
        // @ts-expect-error TextFieldFormik types don't support MenuItem children when used as select
        <TextFieldFormik id={fieldId} label={label} fullWidth select disabled={disabled || isLoading}>
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextFieldFormik>
      )}
    </div>
  );
};

const BOOLEAN_PARAMETER_OPTIONS = [
  { label: 'All', optionValue: null },
  { label: 'True', optionValue: true },
  { label: 'False', optionValue: false },
];

const ParameterField: React.FC<{
  param: ParameterWithQuestionId;
  disabled: boolean;
}> = ({ param, disabled }) => {
  const label = formatParamLabel(param.name);
  const fieldId = `parameters.${param.id}`;

  if (param.values_query_type === 'list') {
    return <ListParameterField param={param} disabled={disabled} />;
  }

  if (param.type.startsWith('date/')) {
    return <DatePickerTextFieldFormik id={fieldId} label={label} fullWidth disabled={disabled} />;
  }

  if (param.type.startsWith('boolean/')) {
    const options = param.required
      ? BOOLEAN_PARAMETER_OPTIONS.filter((opt) => opt.optionValue !== null)
      : BOOLEAN_PARAMETER_OPTIONS;

    return (
      <YesNoNaUnknownQuestionFormik
        id={fieldId}
        questionText={label}
        disabled={disabled}
        // @ts-expect-error options use boolean/null optionValues instead of strings for direct formik value binding
        options={options}
      />
    );
  }

  return <TextFieldFormik id={fieldId} label={label} fullWidth disabled={disabled} />;
};

const ReportParameterFields: React.FC<ReportParameterFieldsProps> = ({ disabled = false }) => {
  const { templates } = useReportTemplates();
  const { values, setFieldValue } = useFormikContext<{
    report_template_id: number | '';
    parameters: ReportParameters;
  }>();

  const selectedTemplate = templates.find((t) => t.id === values.report_template_id);
  const parameters = collectUniqueParameters(selectedTemplate);

  useFormikChangeListener({
    listenForKeys: ['report_template_id'],
    onChange: (currentValues: { report_template_id: number | ''; parameters: ReportParameters }) => {
      const template = templates.find((t) => t.id === currentValues.report_template_id);
      const params = collectUniqueParameters(template);
      const defaults: ReportParameters = {};
      for (const p of params) {
        defaults[p.id] =
          p.values_query_type === 'list' && p.isMultiSelect ? [] : p.type.startsWith('boolean/') ? null : '';
      }
      setFieldValue('parameters', defaults);
    },
    runOnFirstRender: false,
  });

  if (parameters.length === 0) return null;

  return (
    <div className="flex flex-col gap-12">
      <p className="text-sm font-semibold text-slate-700">Filters</p>
      {parameters.map((param) => (
        <ParameterField key={param.id} param={param} disabled={disabled} />
      ))}
    </div>
  );
};

export default ReportParameterFields;
